import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  ButtonSize,
  ButtonSmallSecondary,
  GenericAlert,
  GenericButton,
  Icon,
  IconSVG,
  SVGColorKey,
} from "@studio3t/generic-components";
import { type ReactNode, useState } from "react";
import styled, { useTheme } from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { RawConfigModal } from "@/components/RawConfigModal";
import {
  ConfigActions,
  ConfigBadges,
  ConfigBody,
  ConfigItem,
  ConfigKey,
  ConfigVal,
  SectionCard,
} from "@/components/SectionCard";
import { SkeletonPill } from "@/components/Skeleton";
import type {
  HttpAuth,
  HttpAuthType,
  MongoDbAuth,
  ResourceInfo,
  ScanBound,
  Service,
  ServiceAuth,
} from "@/types/api";
import { describeFieldRules } from "@/utils/fieldRules";

const BADGE = designVariables.ui.badge;
const SEMANTIC = designVariables.ui.semantic;

const HTTP_AUTH_LABELS: Record<HttpAuthType, string> = {
  bearer: "Bearer Token",
  basic: "Basic Auth",
  api_key: "API Key (query param)",
  header: "Custom Header",
};

function assertNever(_value: never): null {
  return null;
}

type AnyAuth = ServiceAuth | HttpAuth | MongoDbAuth;

function isHttpAuth(auth: AnyAuth): auth is HttpAuth {
  return "type" in auth;
}

function isServiceAuth(auth: AnyAuth): auth is ServiceAuth {
  return "kind" in auth && auth.kind !== "x509_inline";
}

function isMongoDbAuth(auth: AnyAuth): auth is MongoDbAuth {
  return "kind" in auth && auth.kind === "x509_inline";
}

// A single label/value row. Renders nothing when the value is empty, so callers
// can list every possible field and only the populated ones show.
function Row({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}): ReactNode {
  if (!value) return null;
  return (
    <ConfigItem>
      <ConfigKey>{label}</ConfigKey>
      <ConfigVal>{value}</ConfigVal>
    </ConfigItem>
  );
}

function HttpAuthRows({ auth }: { auth: HttpAuth }): ReactNode {
  const rows: Array<{ label: string; value: string }> = [
    { label: "Auth Type", value: HTTP_AUTH_LABELS[auth.type] },
  ];
  switch (auth.type) {
    case "bearer":
      rows.push({ label: "Token", value: auth.token });
      break;
    case "basic":
      rows.push({ label: "Username", value: auth.username });
      rows.push({ label: "Password", value: auth.password });
      break;
    case "api_key":
      rows.push({ label: "Key", value: auth.key });
      rows.push({ label: "Param Name", value: auth.param_name });
      break;
    case "header":
      rows.push({ label: "Header Name", value: auth.name });
      rows.push({ label: "Header Value", value: auth.value });
      break;
    default:
      return assertNever(auth);
  }
  return (
    <>
      {rows.map((r) => (
        <Row key={r.label} label={r.label} value={r.value} />
      ))}
    </>
  );
}

function MongoDbAuthRows({ auth }: { auth: MongoDbAuth }): ReactNode {
  return (
    <>
      <Row label="Auth Type" value="x509 Certificates" />
      <Row
        label="Client Certificate"
        value={auth.client_cert ? "••••••" : "—"}
      />
      <Row label="Client Key" value={auth.client_key ? "••••••" : "—"} />
      <Row
        label="CA Certificate"
        value={auth.ca_cert ? "••••••" : "System CA"}
      />
    </>
  );
}

/**
 * The auth block for whichever shape this service's credentials take.
 *
 * One place that picks, rather than three guarded blocks at the call site:
 * the shapes are mutually exclusive, so choosing between them is a single
 * decision and reads as one.
 */
function AuthRows({ auth }: { auth: Service["auth"] }): ReactNode {
  if (!auth) return null;
  if (isMongoDbAuth(auth)) return <MongoDbAuthRows auth={auth} />;
  if (isHttpAuth(auth)) return <HttpAuthRows auth={auth} />;
  if (isServiceAuth(auth)) return <ServiceAuthRows auth={auth} />;
  return null;
}

function ServiceAuthRows({ auth }: { auth: ServiceAuth }): ReactNode {
  return (
    <>
      <Row label="Auth Type" value={auth.kind} />
      <Row label="Account Key" value={auth.account_key_path} />
      <Row label="Env Token" value={auth.env_token_name} />
    </>
  );
}

function resourceIcon(resource: ResourceInfo): Icon {
  switch (resource.resource_type) {
    case "collection":
      return Icon.Collection;
    case "topic":
      return Icon.Lightning;
    case "script":
      return Icon.Code;
    case "route":
      return Icon.Link;
    default:
      return Icon.Document;
  }
}

// What the LLM detail's header offers (Figma 16029-390407 default,
// 16069-170742 non-default): "Set as default" switches the Agent's / AI
// Helper's model, "Delete service" confirms then removes it.
export interface LlmDetailsActions {
  isDefault: boolean;
  // The current default, a lone LLM (the engine falls back to it) and system
  // services can't be removed — the Services list's rule.
  canDelete: boolean;
  settingDefault: boolean;
  onSetDefault: () => void;
  onDelete: () => void;
}

interface DetailsSectionProps {
  service: Service;
  resources: ResourceInfo[] | undefined;
  // True while the resources query is in flight — the Resources row shows
  // skeleton badges instead of appearing only once loaded (layout pop-in).
  resourcesLoading?: boolean;
  getProviderLabel: (provider: Service["provider"]) => string;
  maskConnectionString: (connString?: string) => string;
  // LLM services only: header actions plus the default-model banner.
  llm?: LlmDetailsActions;
}

// Secondary small buttons with a leading icon (no library derivative for
// that shape). The bin is red like every delete affordance; theme.errorIcon
// carries only default/hover colours and IconSVG reads the others with no
// fallback, so the pressed and disabled ones are supplied or the icon
// paints nothing in those states.
function LlmHeaderActions({ llm }: { llm: LlmDetailsActions }) {
  const theme = useTheme();
  const binColors = {
    ...theme.errorIcon,
    active: theme.errorIcon.default,
    disabled: theme.buttonSecondaryDisabledText,
  };
  return (
    <>
      {!llm.isDefault && (
        <GenericButton
          type="secondary"
          size={ButtonSize.Small}
          icon={Icon.Anchor}
          iconPosition="leading"
          text="Set as default"
          loading={llm.settingDefault}
          onClick={llm.onSetDefault}
        />
      )}
      <GenericButton
        type="secondary"
        size={ButtonSize.Small}
        icon={Icon.Bin}
        iconPosition="leading"
        colors={binColors}
        text="Delete service"
        // Also held while "Set as default" is in flight: this service is
        // about to become the default, which can't be deleted.
        disabled={!llm.canDelete || llm.settingDefault}
        onClick={llm.onDelete}
      />
    </>
  );
}

// The banner's own bottom spacing: with the body's row gap it puts the
// design's distance between the alert and the first row.
const DefaultBanner = styled.div`
  width: 100%;
  padding-bottom: ${SEMANTIC.spacingm}px;
`;

// The model as a badge (Figma 16029-390422), like the Resources row.
function ModelRow({ model }: { model: string | undefined }): ReactNode {
  if (!model) return null;
  return (
    <ConfigItem>
      <ConfigKey>Model</ConfigKey>
      <ConfigBadges>
        <BadgeSecondarySubtle>{model}</BadgeSecondarySubtle>
      </ConfigBadges>
    </ConfigItem>
  );
}

// The service's field rows. Every possible field is listed; empty ones render
// nothing (see Row), and the auth block picks the shape for the provider.
function ServiceConfigRows({
  service,
  getProviderLabel,
  maskConnectionString,
}: DetailsSectionProps): ReactNode {
  return (
    <>
      {/* A UDF service's name is its scripts folder, as the Add Service
          form labels it (Figma 14058-10184). */}
      <Row
        label={service.provider === "udf" ? "Folder name" : "Name"}
        value={service.name}
      />
      <Row label="Provider" value={getProviderLabel(service.provider)} />
      <Row
        label="Connection"
        value={
          service.connection_string
            ? maskConnectionString(service.connection_string)
            : undefined
        }
      />
      <Row label="Database" value={service.db_name} />
      <Row label="Project ID" value={service.project_id} />

      <AuthRows auth={service.auth} />

      <Row label="Bootstrap Servers" value={service["bootstrap.servers"]} />
      <Row label="Group ID" value={service["group.id"]} />
      <Row label="Host" value={service.host} />
      <ModelRow model={service.model || service.model_id} />
      <Row label="Script Path" value={service.script_path} />

      {service.provider === "scanner" && <ScannerRows service={service} />}
      {service.provider === "generator" && <GeneratorRows service={service} />}
    </>
  );
}

/**
 * A generator's rate, volume, seed and field rules (TTTL-229).
 *
 * Absent volume and seed are settings, not blanks — unbounded and unseeded —
 * so they read as words. Each ruled field gets its own row, keyed by path.
 */
function GeneratorRows({ service }: { service: Service }): ReactNode {
  return (
    <>
      <Row
        label="Events per second"
        value={
          service.events_per_second === undefined
            ? undefined
            : `${service.events_per_second}/s`
        }
      />
      <Row
        label="Total events"
        value={
          service.total_events === undefined
            ? "Unlimited — runs until the pipeline stops"
            : String(service.total_events)
        }
      />
      <Row
        label="Seed"
        value={
          service.seed === undefined
            ? "Random — a different stream each run"
            : String(service.seed)
        }
      />
      {/* The path rides in the value: the key column is upper-cased, and
          paths are case-sensitive (`firstName`). */}
      {describeFieldRules(service.fields).map((rule) => (
        <Row
          key={rule.path}
          label="Field rule"
          value={`${rule.path}: ${rule.text}`}
        />
      ))}
    </>
  );
}

/**
 * A scanner's traversal settings.
 *
 * Grouped rather than folded into the list above because one of them needs a
 * sentence rather than a value: whether the scan ever ends is decided by `to`
 * alone, and an absent `to` is the setting — not a blank.
 */
function ScannerRows({ service }: { service: Service }): ReactNode {
  const filter = service.filter ?? {};
  const filterEntries = Object.entries(filter);

  return (
    <>
      <Row label="Target Service" value={service.target_service} />
      <Row label="Order By" value={service.order_by} />
      <Row label="From" value={formatBound(service.from)} />
      <Row label="To" value={formatBound(service.to)} />
      <Row
        label="Ends"
        value={
          service.to === undefined
            ? "No — reads everything, then follows new documents"
            : "Yes — stops at the upper bound"
        }
      />
      <Row
        label="Filter"
        value={
          filterEntries.length > 0
            ? filterEntries.map(([k, v]) => `${k} = ${String(v)}`).join(", ")
            : undefined
        }
      />
      <Row
        label="Page Size"
        value={
          service.page_size === undefined
            ? undefined
            : String(service.page_size)
        }
      />
    </>
  );
}

/**
 * A bound as text, keeping `false` and `0` visible.
 *
 * `Row` hides falsy values, so returning them unconverted would drop a
 * legitimate bound of zero and show nothing where a setting exists.
 */
function formatBound(value: ScanBound | undefined): string | undefined {
  return value === undefined ? undefined : String(value);
}

// Badge-shaped placeholders while the resources fetch is in flight (it can be
// slow — the backend connects to the live service to list them).
const SKELETON_BADGES = ["a", "b", "c"];

function ResourcesRow({
  resources,
  loading = false,
}: {
  resources: ResourceInfo[] | undefined;
  loading?: boolean;
}): ReactNode {
  if (loading && !resources) {
    return (
      <ConfigItem aria-hidden="true">
        <ConfigKey>Resources</ConfigKey>
        <ConfigBadges>
          {SKELETON_BADGES.map((key) => (
            <SkeletonPill key={key} $width="88px" />
          ))}
        </ConfigBadges>
      </ConfigItem>
    );
  }
  if (!resources || resources.length === 0) return null;
  return (
    <ConfigItem>
      <ConfigKey>Resources</ConfigKey>
      <ConfigBadges>
        {resources.map((r) => (
          <BadgeSecondarySubtle key={r.name}>
            <IconSVG
              icon={resourceIcon(r)}
              size={BADGE.icon}
              svgColorKey={SVGColorKey.semanticTextBody}
            />
            {r.name}
          </BadgeSecondarySubtle>
        ))}
      </ConfigBadges>
    </ConfigItem>
  );
}

export function DetailsSection({
  service,
  resources,
  resourcesLoading = false,
  getProviderLabel,
  maskConnectionString,
  llm,
}: DetailsSectionProps) {
  const [showRawConfig, setShowRawConfig] = useState(false);

  return (
    <SectionCard
      title="Details"
      actions={llm ? <LlmHeaderActions llm={llm} /> : undefined}
    >
      {/* Custom scrollbars when a long value (connection string, script path) is
          wider than the card on a narrow screen. */}
      <OverlayScroll>
        <ConfigBody>
          {llm?.isDefault && (
            <DefaultBanner>
              <GenericAlert
                state="Info"
                icon
                dismissible={false}
                width="100%"
                message="This is the default model used by the Agent and the AI Helper."
              />
            </DefaultBanner>
          )}
          <ServiceConfigRows
            service={service}
            resources={resources}
            getProviderLabel={getProviderLabel}
            maskConnectionString={maskConnectionString}
          />
          <ResourcesRow resources={resources} loading={resourcesLoading} />

          <ConfigActions>
            <ButtonSmallSecondary
              text="Raw configuration"
              active={showRawConfig}
              onClick={() => setShowRawConfig(true)}
            />
          </ConfigActions>
        </ConfigBody>
      </OverlayScroll>

      {showRawConfig && (
        <RawConfigModal
          isOpen
          value={JSON.stringify(service, null, 2)}
          onClose={() => setShowRawConfig(false)}
        />
      )}
    </SectionCard>
  );
}
